// src/utils/profileCompletion.ts
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ProfileCompletionStatus } from '../components/profile/ProfileUpdateCard';

/**
 * Check if a user's profile is complete and return missing fields
 * @param userId - The user's ID
 * @param displayName - The user's display name from auth (fallback)
 * @param photoURL - The user's photo URL from auth (fallback)
 * @returns ProfileCompletionStatus with completion percentage and missing fields
 */
export const checkProfileCompletion = async (
   userId: string,
   displayName: string | null,
   photoURL: string | null
): Promise<ProfileCompletionStatus> => {
   try {
      // Fetch user profile data from Firestore
      const profileDoc = await getDoc(doc(db, 'userProfiles', userId));
      const profileData = profileDoc.exists() ? profileDoc.data() : {};

      // Use displayName and photoURL from userProfiles if available, otherwise use auth values
      const actualDisplayName = profileData.displayName || displayName;
      const actualPhotoURL = profileData.photoURL || photoURL;

      // Check each field
      const hasDisplayName = !!(
         actualDisplayName &&
         actualDisplayName.trim() !== '' &&
         actualDisplayName !== 'Unknown User' &&
         !actualDisplayName.startsWith('User ')
      );

      const hasPhotoURL = !!(actualPhotoURL && actualPhotoURL.trim() !== '');

      const hasBio = !!(profileData.bio && profileData.bio.trim() !== '');

      const hasLanguages = !!(
         profileData.languages && 
         (Array.isArray(profileData.languages) 
            ? profileData.languages.length > 0 
            : profileData.languages.trim() !== '')
      );

      const hasTagline = !!(profileData.tagline && profileData.tagline.trim() !== '');

      const hasCompanyName = !!(profileData.companyName && profileData.companyName.trim() !== '');

      const hasWebsite = !!(profileData.website && profileData.website.trim() !== '');

      const hasPhoneNumber = !!(profileData.phoneNumber && profileData.phoneNumber.trim() !== '');

      const hasLocation = !!(profileData.location && profileData.location.trim() !== '');

      const hasWhatIDo = !!(
         profileData.whatIDo && 
         Array.isArray(profileData.whatIDo) && 
         profileData.whatIDo.length > 0
      );

      const hasLookingFor = !!(
         profileData.lookingFor && 
         Array.isArray(profileData.lookingFor) && 
         profileData.lookingFor.length > 0
      );

      const hasFeaturedProjects = !!(
         profileData.featuredProjects && 
         Array.isArray(profileData.featuredProjects) && 
         profileData.featuredProjects.length > 0
      );

      const hasInterests = !!(
         profileData.interests && 
         Array.isArray(profileData.interests) && 
         profileData.interests.filter((i: any) => i.selected).length > 0
      );

      // Calculate completion percentage (each field is worth ~7.69% - 13 fields total)
      const completedFields = [
         hasDisplayName, 
         hasPhotoURL, 
         hasBio, 
         hasLanguages,
         hasTagline,
         hasCompanyName,
         hasWebsite,
         hasPhoneNumber,
         hasLocation,
         hasWhatIDo,
         hasLookingFor,
         hasFeaturedProjects,
         hasInterests
      ].filter(Boolean).length;
      const completionPercentage = Math.round((completedFields / 13) * 100);

      // Build list of missing fields
      const missingFields: string[] = [];
      if (!hasDisplayName) missingFields.push('Display Name');
      if (!hasPhotoURL) missingFields.push('Profile Photo');
      if (!hasBio) missingFields.push('Bio');
      if (!hasLocation) missingFields.push('Location');
      if (!hasLanguages) missingFields.push('Languages');
      if (!hasTagline) missingFields.push('Tagline');
      if (!hasCompanyName) missingFields.push('Company');
      if (!hasWebsite) missingFields.push('Website');
      if (!hasPhoneNumber) missingFields.push('Phone Number');
      if (!hasWhatIDo) missingFields.push('What I Do');
      if (!hasLookingFor) missingFields.push('Looking For');
      if (!hasFeaturedProjects) missingFields.push('Featured Projects');
      if (!hasInterests) missingFields.push('Interests & Expertise');

      return {
         hasDisplayName,
         hasPhotoURL,
         hasBio,
         hasLanguages,
         hasTagline,
         hasCompanyName,
         hasWebsite,
         hasPhoneNumber,
         hasLocation,
         hasWhatIDo,
         hasLookingFor,
         hasFeaturedProjects,
         hasInterests,
         completionPercentage,
         missingFields,
      };
   } catch (error) {
      console.error('Error checking profile completion:', error);
      // Return default incomplete status on error
      return {
         hasDisplayName: false,
         hasPhotoURL: false,
         hasBio: false,
         hasLanguages: false,
         hasTagline: false,
         hasCompanyName: false,
         hasWebsite: false,
         hasPhoneNumber: false,
         hasLocation: false,
         hasWhatIDo: false,
         hasLookingFor: false,
         hasFeaturedProjects: false,
         hasInterests: false,
         completionPercentage: 0,
         missingFields: ['Display Name', 'Profile Photo', 'Bio', 'Location', 'Languages', 'Tagline', 'Company', 'Website', 'Phone Number', 'What I Do', 'Looking For', 'Featured Projects', 'Interests & Expertise'],
      };
   }
};

